import React, { useState, useEffect } from 'react';
import SoftGrid from '../Shared/SoftGrid/SoftGrid';
import ApiServices from '../Services/ApiServices';
import { toastHelper } from '../../Helpers/toastHelper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus,
    faPencilAlt,
    faTrash,
    faPowerOff,
    faCalendarAlt,
    faTags,
    faUsers
} from '@fortawesome/free-solid-svg-icons';
import DeleteAlert from '../Shared/DeleteAlert';
import ModalPopUp from '../Shared/ModalPopUp';
import { useForm } from 'react-hook-form';

const CouponManager = () => {
    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteDialog, setDeleteDialog] = useState({ open: false, coupon: null });

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingCoupon, setEditingCoupon] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm();

    const fetchCoupons = async () => {
        setLoading(true);
        try {
            const res = await ApiServices.get('Coupon/getAll');
            if (res && res.data) {
                setCoupons(res.data);
            } else if (Array.isArray(res)) {
                setCoupons(res);
            }
        } catch (e) {
            toastHelper.error('فشل في جلب الكوبونات');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCoupons();
    }, []);

    const handleDelete = async () => {
        try {
            setLoading(true);
            const couponCode = deleteDialog.coupon.coupon_code;
            const response = await ApiServices.delete(`Coupon/delete/${couponCode}`);
            if (response && response.check) {
                toastHelper.success('تم حذف الكوبون بنجاح');
                fetchCoupons();
            } else {
                toastHelper.error(response.message || 'فشل في حذف الكوبون');
            }
        } catch (error) {
            toastHelper.error('حدث خطأ أثناء الحذف');
        } finally {
            setLoading(false);
            setDeleteDialog({ open: false, coupon: null });
        }
    };

    const handleToggleActive = async (coupon) => {
        try {
            setLoading(true);
            const res = await ApiServices.put(`Coupon/activeInActive/${coupon.coupon_code}`);
            if (res && res.check) {
                toastHelper.success(`تم ${coupon.is_active ? 'إلغاء تفعيل' : 'تفعيل'} الكوبون بنجاح`);
                fetchCoupons();
            } else {
                toastHelper.error(res.message || 'فشل في تحديث حالة الكوبون');
            }
        } catch (error) {
            toastHelper.error('فشل في تحديث حالة الكوبون');
        } finally {
            setLoading(false);
        }
    };

    const openAddModal = () => {
        setEditingCoupon(null);
        reset({
            coupon_code: '',
            discount_amount: 0,
            valid_from: '',
            valid_to: '',
            target_entity: '',
            no_of_uses: 1
        });
        setIsModalOpen(true);
    };

    const openEditModal = (coupon) => {
        setEditingCoupon(coupon);
        // Format dates for input[type="datetime-local"] if necessary, 
        // assuming dates come as ISO strings
        const formatForInput = (dateString) => {
            if (!dateString) return '';
            const date = new Date(dateString);
            return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
        };

        reset({
            coupon_code: coupon.coupon_code || '',
            discount_amount: coupon.discount_amount || 0,
            valid_from: formatForInput(coupon.valid_from),
            valid_to: formatForInput(coupon.valid_to),
            target_entity: coupon.target_entity || '',
            no_of_uses: coupon.no_of_uses || 1
        });
        setIsModalOpen(true);
    };

    const onSubmitForm = async (data) => {
        try {
            setSubmitting(true);

            // Format dates back to ISO
            const payload = {
                ...data,
                discount_amount: Number(data.discount_amount),
                no_of_uses: Number(data.no_of_uses),
                valid_from: new Date(data.valid_from).toISOString(),
                valid_to: new Date(data.valid_to).toISOString()
            };

            let response;
            if (editingCoupon) {
                // Adjust update endpoint as per your API standard
                response = await ApiServices.put(`Coupon/update`, payload);
            } else {
                response = await ApiServices.post('Coupon/create', payload);
            }

            if (response && response.check !== false) { // check if API returns false on fail
                toastHelper.success(editingCoupon ? 'تم تحديث الكوبون بنجاح' : 'تم إضافة الكوبون بنجاح');
                setIsModalOpen(false);
                fetchCoupons();
            } else {
                toastHelper.error(response.message || 'فشل في حفظ الكوبون');
            }
        } catch (error) {
            toastHelper.error('حدث خطأ أثناء حفظ الكوبون');
        } finally {
            setSubmitting(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const columns = [
        {
            key: 'coupon_code',
            label: 'كود الخصم',
            sortable: true,
            render: (val) => (
                <div className="coupon-code-cell">
                    <FontAwesomeIcon icon={faTags} className="coupon-icon" />
                    <span className="code">{val}</span>
                </div>
            )
        },
        {
            key: 'discount_amount',
            label: 'الخصم',
            sortable: true,
            render: (val) => (
                <span className="discount-amount">{val}</span>
            )
        },
        {
            key: 'validity',
            label: 'فترة الصلاحية',
            render: (_, row) => (
                <div className="validity-cell">
                    <div>من: {formatDate(row.valid_from)}</div>
                    <div>إلى: {formatDate(row.valid_to)}</div>
                </div>
            )
        },
        {
            key: 'no_of_uses',
            label: 'مرات الاستخدام',
            render: (val) => <span className="stat-badge"><FontAwesomeIcon icon={faUsers} /> {val || 0}</span>
        },
        {
            key: 'target_entity',
            label: 'الكيان المستهدف'
        },
        {
            key: 'is_active',
            label: 'الحالة',
            render: (val, row) => {
                const now = new Date();
                now.setHours(0, 0, 0, 0);
                const validFrom = new Date(row.valid_from);
                validFrom.setHours(0, 0, 0, 0);
                const validTo = new Date(row.valid_to);
                validTo.setHours(0, 0, 0, 0);

                const isExpired = validTo < now;
                const isActive = row.is_active && validFrom <= now && validTo >= now;

                if (isExpired) return <span className="status-badge expired">منتهي الصلاحية</span>;
                if (isActive) return <span className="status-badge active">نشط</span>;
                return <span className="status-badge inactive">غير نشط</span>;
            }
        }
    ];

    const actions = (row) => [
        {
            label: 'تعديل',
            icon: <FontAwesomeIcon icon={faPencilAlt} />,
            onClick: (row) => openEditModal(row),
            className: 'edit-btn'
        },
        {
            label: row.is_active ? 'إلغاء تفعيل' : 'تفعيل',
            icon: <FontAwesomeIcon icon={faPowerOff} />,
            onClick: (row) => handleToggleActive(row),
            className: row.is_active ? 'toggle-btn-inactive' : 'toggle-btn-active'
        },
        {
            label: 'حذف',
            icon: <FontAwesomeIcon icon={faTrash} />,
            onClick: (row) => setDeleteDialog({ open: true, coupon: row }),
            className: 'delete-btn'
        }
    ];

    return (
        <div className="admin-coupons-container">
            <div className="page-header">
                <div className="header-info">
                    <h2>إدارة الكوبونات</h2>
                    <p>إدارة أكواد الخصم والفترات المتاحة لها ({coupons.length})</p>
                </div>
                <button className="add-coupon-btn" onClick={openAddModal}>
                    <FontAwesomeIcon icon={faPlus} />
                    <span>إضافة كوبون جديد</span>
                </button>
            </div>

            <SoftGrid
                columns={columns}
                data={coupons}
                isLoading={loading}
                actions={actions}
                searchPlaceholder="ابحث عن كوبون..."
            />

            <DeleteAlert
                isOpen={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, coupon: null })}
                onConfirm={handleDelete}
                itemName={`الكوبون ${deleteDialog.coupon?.coupon_code}`}
                loading={loading}
            />

            <ModalPopUp
                isOpen={isModalOpen}
                onClose={() => !submitting && setIsModalOpen(false)}
                title={editingCoupon ? 'تعديل كوبون' : 'إضافة كوبون جديد'}
                showModalButtons={false}
            >
                <form onSubmit={handleSubmit(onSubmitForm)} className="coupon-form">
                    <div className="form-group">
                        <label>كود الخصم</label>
                        <input
                            {...register("coupon_code", { required: "كود الخصم مطلوب" })}
                            placeholder="مثال: SUMMER2026"
                            className={`form-control ${errors.coupon_code ? 'is-invalid' : ''}`}
                            disabled={!!editingCoupon}
                        />
                        {errors.coupon_code && <span className="error-text">{errors.coupon_code.message}</span>}
                    </div>

                    <div className="form-row">
                        <div className="form-group half-width">
                            <label>قيمة الخصم</label>
                            <input
                                type="number"
                                step="any"
                                {...register("discount_amount", { required: "قيمة الخصم مطلوبة", min: { value: 0, message: "يجب أن يكون أكبر من أو يساوي صفر" } })}
                                placeholder="مثال: 50"
                                className={`form-control ${errors.discount_amount ? 'is-invalid' : ''}`}
                            />
                            {errors.discount_amount && <span className="error-text">{errors.discount_amount.message}</span>}
                        </div>

                        <div className="form-group half-width">
                            <label>عدد مرات الاستخدام</label>
                            <input
                                type="number"
                                {...register("no_of_uses", { required: "عدد المرات مطلوب", min: { value: 1, message: "يجب أن يكون 1 على الأقل" } })}
                                placeholder="مثال: 10"
                                className={`form-control ${errors.no_of_uses ? 'is-invalid' : ''}`}
                            />
                            {errors.no_of_uses && <span className="error-text">{errors.no_of_uses.message}</span>}
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group half-width">
                            <label>صالح من</label>
                            <input
                                type="datetime-local"
                                {...register("valid_from", { required: "تاريخ البدء مطلوب" })}
                                className={`form-control ${errors.valid_from ? 'is-invalid' : ''}`}
                            />
                            {errors.valid_from && <span className="error-text">{errors.valid_from.message}</span>}
                        </div>

                        <div className="form-group half-width">
                            <label>صالح إلى</label>
                            <input
                                type="datetime-local"
                                {...register("valid_to", { required: "تاريخ الانتهاء مطلوب" })}
                                className={`form-control ${errors.valid_to ? 'is-invalid' : ''}`}
                            />
                            {errors.valid_to && <span className="error-text">{errors.valid_to.message}</span>}
                        </div>
                    </div>

                    <div className="form-group">
                        <label>الكيان المستهدف (اختياري)</label>
                        <input
                            {...register("target_entity")}
                            placeholder="مثال: Course123"
                            className="form-control"
                        />
                    </div>

                    <div className="modal-actions mt-4">
                        <button type="button" className="btn btn-secondary" onClick={() => setIsModalOpen(false)} disabled={submitting}>
                            إلغاء
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={submitting}>
                            {submitting ? 'جاري الحفظ...' : 'حفظ الكوبون'}
                        </button>
                    </div>
                </form>
            </ModalPopUp>

            <style>{`
                .admin-coupons-container {
                    padding: 20px;
                    direction: rtl;
                }
                .page-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 24px;
                }
                .header-info h2 {
                    margin: 0;
                    color: #1e293b;
                    font-size: 1.5rem;
                    font-weight: 800;
                }
                .header-info p {
                    margin: 4px 0 0;
                    color: #64748b;
                }
                .add-coupon-btn {
                    background: #6366f1;
                    color: white;
                    border: none;
                    padding: 12px 24px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 0.2s;
                    box-shadow: 0 4px 6px -1px rgba(99, 102, 241, 0.4);
                }
                .add-coupon-btn:hover {
                    background: #4f46e5;
                    transform: translateY(-1px);
                }
                
                .coupon-code-cell {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
                .coupon-icon {
                    color: #8b5cf6;
                    font-size: 1.1rem;
                }
                .code {
                    font-weight: 700;
                    color: #334155;
                    font-size: 1.1rem;
                }
                
                .discount-amount {
                    font-weight: 800;
                    color: #10b981;
                    font-size: 1.1rem;
                }

                .validity-cell {
                    display: flex;
                    flex-direction: column;
                    font-size: 0.9rem;
                    color: #475569;
                    gap: 4px;
                }

                .stat-badge {
                    background: #f1f5f9;
                    color: #475569;
                    padding: 4px 10px;
                    border-radius: 8px;
                    font-weight: 700;
                    font-size: 0.9rem;
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                }
                
                .status-badge {
                    padding: 4px 10px;
                    border-radius: 8px;
                    font-weight: 700;
                    font-size: 0.85rem;
                }
                .status-badge.active {
                    background: #dcfce7;
                    color: #166534;
                }
                .status-badge.inactive {
                    background: #fee2e2;
                    color: #991b1b;
                }
                .status-badge.expired {
                    background: #f3f4f6;
                    color: #374151;
                }

                /* Actions Colors */
                .edit-btn { color: #3b82f6 !important; }
                .delete-btn { color: #ef4444 !important; }
                .toggle-btn-active { color: #10b981 !important; }
                .toggle-btn-inactive { color: #6366f1 !important; }

                /* Modal Form Styles */
                .coupon-form {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                    text-align: right;
                    direction: rtl;
                }
                .form-row {
                    display: flex;
                    gap: 16px;
                }
                .half-width {
                    flex: 1;
                }
                .form-group label {
                    font-weight: 600;
                    margin-bottom: 8px;
                    display: block;
                    color: #334155;
                }
                .form-control {
                    width: 100%;
                    padding: 10px 14px;
                    border: 1px solid #cbd5e1;
                    border-radius: 8px;
                    font-size: 1rem;
                    transition: border-color 0.2s;
                }
                .form-control:focus {
                    outline: none;
                    border-color: #6366f1;
                    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
                }
                .is-invalid {
                    border-color: #ef4444;
                }
                .error-text {
                    color: #ef4444;
                    font-size: 0.85rem;
                    margin-top: 4px;
                    display: block;
                }
                .modal-actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: 12px;
                    margin-top: 24px;
                }
                .btn {
                    padding: 10px 20px;
                    border-radius: 8px;
                    font-weight: 600;
                    cursor: pointer;
                    border: none;
                }
                .btn-secondary {
                    background: #f1f5f9;
                    color: #475569;
                }
                .btn-secondary:hover {
                    background: #e2e8f0;
                }
                .btn-primary {
                    background: #6366f1;
                    color: white;
                }
                .btn-primary:hover {
                    background: #4f46e5;
                }
                .btn:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                }
            `}</style>
        </div>
    );
};

export default CouponManager;
